import { HttpHeaders } from '@angular/common/http';
import { OnDestroy, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SessionUser } from 'app/core/models';
import { selectSessionUser, State } from 'app/core/store';

/**
 * Service to add http headers to a http request performed by HttpRequestService
 *
 * @export
 */
@Injectable()
export class HttpRequestHeadersService implements OnDestroy {
    /** @property token - string that holds JWT token obtained by AuthenticationService
     */
    private token: string;

    /** @property tenant - string that holds current tenant that will be used to define tenant header
     */
    tenant: string;

    /** @property httpHeaders - HttpHeaders variable with desired headers
     */
    private httpHeaders: HttpHeaders;

    /** @property headers - The options provided to a HttpClient's request that holds httpHeaders
     */
    headers: { headers?: HttpHeaders };

    /** Emits whenever the service is destroyed. */
    private readonly destroy$: Subject<void> = new Subject<void>();

    private sessionUser$: Observable<SessionUser>;

    private sessionUser: SessionUser;

    /** @param store - Store is the dependency that
     * allows us to add an authorization header with JWT token provided by this service
     */
    constructor(private store: Store<State>) {
        this.httpHeaders = new HttpHeaders();

        this.sessionUser$ = this.store.select(selectSessionUser);
        this.sessionUser$.pipe(takeUntil(this.destroy$)).subscribe((sessionUser: SessionUser) => {
            if (sessionUser) {
                this.sessionUser = sessionUser;
                this.tenant = sessionUser.tenantId;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Set http authorization headers
     */
    async setAuthorizationHeaders(setContentType: boolean = true): Promise<void> {
        await this.fetchToken();

        if (this.token !== null && this.token !== undefined) {
            /** add JWT Token to Authorization Header */
            this.httpHeaders = this.httpHeaders.set('Authorization', `Bearer ${this.token}`);
        }

        if (this.tenant !== null && this.tenant !== undefined) {
            /** add Tenant Information to Authorization Header */
            this.httpHeaders = this.httpHeaders.set('x-tenant', this.tenant);
        }

        if (setContentType) {
            this.httpHeaders = this.httpHeaders.set('Content-Type', 'application/json');
        } else {
            this.httpHeaders = this.httpHeaders.delete('Content-Type');
        }

        this.headers = { headers: this.httpHeaders };
    }

    /**
     * Fetch token from current auth context's session user
     */
    async fetchToken(): Promise<void> {
        if (this.sessionUser) {
            this.token = await this.sessionUser.getSessionToken();
        }
    }

    /**
     * Set http headers
     * @param headers - The map with all desired headers that will be added to header object.
     */
    async setHttpHeaders(headers: Map<string, string>, reset: boolean = true): Promise<void> {
        if (reset) {
            await this.resetHttpHeaders();
        }

        headers.forEach((value, key) => (this.httpHeaders = this.httpHeaders.set(key, value)));

        this.headers = { headers: this.httpHeaders };
    }

    /**
     * Delete http headers
     * @param headers - The map with all desired headers that will be deleted to header object.
     */
    async deleteHttpHeaders(headers: Map<string, string>, reset: boolean = true): Promise<void> {
        if (reset) {
            await this.resetHttpHeaders();
        } else {
            headers.forEach((value, key) => {
                this.httpHeaders = this.httpHeaders.delete(key);
            });

            this.headers = { headers: this.httpHeaders };
        }
    }

    /**
     * Reset http headers
     */
    async resetHttpHeaders(): Promise<void> {
        this.httpHeaders = new HttpHeaders();

        await this.setAuthorizationHeaders();
    }
}
